import { useMemo } from 'react';
import cs from 'classnames';
import { format, parseISO } from 'date-fns';
import { FIXTURE_MATCH_DATE } from '../../constants';
import { ClubNameType } from '../../contracts';
import { AttendancePlacement, Fixture } from '../Fixture/Fixture';
import { LinkSize, LinkVariant } from '../../../common/components/Link/contracts';
import Link from '../../../common/components/Link/Link';
import { SectionHeader } from '../../../common/components/SectionHeader';
import { Match, MatchStatus } from '@repo/types';

const FIXTURE_LIST_CLASS = 'fixture-match-list';

const FINISHED_STATUSES: Set<MatchStatus> = new Set([
  MatchStatus.FullTime,
  MatchStatus.FullTimeAfterExtraTime,
  MatchStatus.Abandoned,
  MatchStatus.Postponed,
]);

export type FixtureListUIProps = {
  fixtures: Match[];
  groupByDate: boolean;
  showAttendance?: boolean;
  attendancePlacement?: AttendancePlacement;
  groupByCompetition?: boolean;
  competitionsSort?: string[];
  nameType?: ClubNameType;
  className?: string;
  style?: React.CSSProperties;
  columns?: number;
  linkLabel?: string;
  linkUrl?: string;
  linkTargetBlank?: boolean;
  linkVariant?: LinkVariant;
  linkSize?: LinkSize;
  isFixtureExpandable?: boolean;
  /** A string is the CMS's rich-text HTML; a node lets a parent that owns the heading pass its
   * own markup, so title and link end up in the same header row (SC-1143). */
  title?: React.ReactNode;
  sortByDate?: 'ASC' | 'DESC';
  allFixturesExpanded?: boolean;
};

type FixturesByDate = {
  [date: string]: Match[];
};

function FixtureListEmptyState({ className }: { className?: string }) {
  return (
    <div className={cs(`${FIXTURE_LIST_CLASS}__empty`, className)} role="status">
      <svg
        className="fixture-match-list__empty-icon"
        width={48}
        height={48}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth={1.5} />
        <path
          d="M12 8.4 15.4 10.9 14.1 14.9 9.9 14.9 8.6 10.9Z"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        <path
          d="M12 8.4V3.2M15.4 10.9 20.3 9.2M14.1 14.9 17.2 19.2M9.9 14.9 6.8 19.2M8.6 10.9 3.7 9.2"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
        />
      </svg>
      <p className="fixture-match-list__empty-title text-m">No fixtures yet</p>
      <p className="fixture-match-list__empty-text">Check back soon — new matches will appear here.</p>
    </div>
  );
}

type FixturesByCompetition = {
  [competitionId: string]: { competition: Match['competition']; fixtures: Match[] };
};

// ── Shared helpers ────────────────────────────────────────────

function getTeamNameForType(team: Match['homeTeam'] | Match['awayTeam'], nameType?: ClubNameType): string {
  switch (nameType) {
    case ClubNameType.ShortName:
      return team.shortName;
    case ClubNameType.ThreeLetterName:
      return team.threeLetterName ?? team.shortName;
    case ClubNameType.NickName:
      return team.nickName ?? team.fullName;
    case ClubNameType.FullName:
    default:
      return team.fullName;
  }
}

function groupFixturesByCompetition(fixtures: Match[]): FixturesByCompetition {
  return fixtures.reduce((acc, fixture) => {
    if (!acc[fixture.competition.id]) {
      acc[fixture.competition.id] = { competition: fixture.competition, fixtures: [fixture] };
    } else {
      acc[fixture.competition.id]!.fixtures.push(fixture);
    }
    return acc;
  }, {} as FixturesByCompetition);
}

function sortCompetitionIds(ids: string[], competitionsSort?: string[]): string[] {
  return ids.sort((a, b) => {
    const indexA = competitionsSort?.findIndex((id) => a === id) ?? -1;
    const indexB = competitionsSort?.findIndex((id) => b === id) ?? -1;
    const orderA = indexA === -1 ? Infinity : indexA;
    const orderB = indexB === -1 ? Infinity : indexB;
    return orderA - orderB;
  });
}

function sortFixtures(
  fixtures: Match[],
  getTeamName: (team: Match['homeTeam'] | Match['awayTeam']) => string
): Match[] {
  const allFinished = fixtures.length > 0 && fixtures.every((match) => FINISHED_STATUSES.has(match.status));

  return [...fixtures].sort((a, b) => {
    if (allFinished) {
      const homeTeamComparison = getTeamName(a.homeTeam).localeCompare(getTeamName(b.homeTeam));
      if (homeTeamComparison !== 0) return homeTeamComparison;
      return getTeamName(a.awayTeam).localeCompare(getTeamName(b.awayTeam));
    }

    const dateComparison = new Date(a.date).getTime() - new Date(b.date).getTime();
    if (dateComparison !== 0 || !a.time || !b.time) return dateComparison;

    const timeComparison = a.time.localeCompare(b.time);
    if (timeComparison !== 0) return timeComparison;

    const homeTeamComparison = getTeamName(a.homeTeam).localeCompare(getTeamName(b.homeTeam));
    if (homeTeamComparison !== 0) return homeTeamComparison;

    return getTeamName(a.awayTeam).localeCompare(getTeamName(b.awayTeam));
  });
}

// ── Sub-components ────────────────────────────────────────────

type FixtureItemProps = {
  fixture: Match;
  showAttendance?: boolean;
  attendancePlacement?: AttendancePlacement;
  nameType?: ClubNameType;
  isFixtureExpandable?: boolean;
  allFixturesExpanded?: boolean;
};

const FixtureItem = ({
  fixture,
  showAttendance,
  attendancePlacement,
  nameType,
  isFixtureExpandable,
  allFixturesExpanded,
}: FixtureItemProps) => (
  <li className={`${FIXTURE_LIST_CLASS}__item`}>
    <Fixture
      isExpanded={allFixturesExpanded}
      className={`${FIXTURE_LIST_CLASS}__fixture-match`}
      fixture={fixture}
      showAttendance={showAttendance}
      attendancePlacement={attendancePlacement}
      nameType={nameType}
      isFixtureExpandable={isFixtureExpandable}
    />
  </li>
);

type DateGroupByCompetitionProps = {
  date: string;
  formattedDate: string;
  fixtures: Match[];
  competitionsSort?: string[];
  style: React.CSSProperties;
  className?: string;
  showAttendance?: boolean;
  attendancePlacement?: AttendancePlacement;
  nameType?: ClubNameType;
  isFixtureExpandable?: boolean;
  allFixturesExpanded?: boolean;
};

const DateGroupByCompetition = ({
  date,
  formattedDate,
  fixtures,
  competitionsSort,
  style,
  className,
  showAttendance,
  attendancePlacement,
  nameType,
  isFixtureExpandable,
  allFixturesExpanded,
}: DateGroupByCompetitionProps) => {
  const fixturesByCompetition = groupFixturesByCompetition(fixtures);
  const sortedIds = sortCompetitionIds(Object.keys(fixturesByCompetition), competitionsSort);

  return (
    <div className={cs('fixture-match-list-by-competition', className)} key={date}>
      <h4 className="fixture-match-list-by-competition__date-title">{formattedDate}</h4>
      <ul
        style={style}
        className={cs('fixture-match-list-by-competition__competitions-list', 'fixture-match-list-by-date__match-list')}
      >
        {sortedIds.map((competitionId) => {
          const { competition, fixtures: compFixtures } = fixturesByCompetition[competitionId]!;
          return (
            <li key={competitionId}>
              <h4 className="fixture-match-list-by-competition__competition-title">
                <span className="text-2xs">{competition.name}</span>
              </h4>
              <ul>
                {compFixtures.map((fixture) => (
                  <FixtureItem
                    key={fixture.id}
                    fixture={fixture}
                    showAttendance={showAttendance}
                    attendancePlacement={attendancePlacement}
                    nameType={nameType}
                    isFixtureExpandable={isFixtureExpandable}
                    allFixturesExpanded={allFixturesExpanded}
                  />
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

type DateGroupByDateProps = {
  date: string;
  formattedDate: string;
  fixtures: Match[];
  style: React.CSSProperties;
  className?: string;
  showAttendance?: boolean;
  attendancePlacement?: AttendancePlacement;
  nameType?: ClubNameType;
  isFixtureExpandable?: boolean;
  allFixturesExpanded?: boolean;
};

const DateGroupByDate = ({
  date,
  formattedDate,
  fixtures,
  style,
  className,
  showAttendance,
  attendancePlacement,
  nameType,
  isFixtureExpandable,
  allFixturesExpanded,
}: DateGroupByDateProps) => (
  <div className={cs('fixture-match-list-by-date', className)} key={date}>
    <h4 className="fixture-match-list-by-date__date-title">{formattedDate}</h4>
    <ul style={style} className={cs(FIXTURE_LIST_CLASS, 'fixture-match-list-by-date__match-list')}>
      {fixtures.map((fixture) => (
        <FixtureItem
          key={fixture.id}
          fixture={fixture}
          showAttendance={showAttendance}
          attendancePlacement={attendancePlacement}
          nameType={nameType}
          isFixtureExpandable={isFixtureExpandable}
          allFixturesExpanded={allFixturesExpanded}
        />
      ))}
    </ul>
  </div>
);

// ── Grouped-by-date rendering ─────────────────────────────────

type FixtureListGroupedProps = {
  sortedFixtures: Match[];
  sortByDate?: 'ASC' | 'DESC';
  groupByCompetition: boolean;
  competitionsSort?: string[];
  style: React.CSSProperties;
  className?: string;
  showAttendance?: boolean;
  attendancePlacement?: AttendancePlacement;
  nameType?: ClubNameType;
  isFixtureExpandable?: boolean;
  allFixturesExpanded?: boolean;
  /** The already-assembled `SectionHeader`, built once by the parent so the flat and grouped
   * views cannot drift in how they render the title + link row (SC-1143). */
  header: React.ReactNode;
};

function FixtureListGrouped({
  sortedFixtures,
  sortByDate,
  groupByCompetition,
  competitionsSort,
  style,
  className,
  showAttendance,
  attendancePlacement,
  nameType,
  isFixtureExpandable,
  allFixturesExpanded,
  header,
}: FixtureListGroupedProps) {
  const fixturesByDate: FixturesByDate = useMemo(() => {
    return sortedFixtures.reduce((acc, fixture) => {
      if (!acc[fixture.date]) {
        acc[fixture.date] = [fixture];
      } else {
        acc[fixture.date]!.push(fixture);
      }
      return acc;
    }, {} as FixturesByDate);
  }, [sortedFixtures]);

  const sharedItemProps = { showAttendance, attendancePlacement, nameType, isFixtureExpandable, allFixturesExpanded };
  const sortedDateEntries = Object.entries(fixturesByDate).sort((a, b) =>
    sortByDate === 'DESC' ? b[0].localeCompare(a[0]) : a[0].localeCompare(b[0])
  );

  return (
    <div className={FIXTURE_LIST_CLASS}>
      {header}
      <div className={`${FIXTURE_LIST_CLASS}__wrapper`}>
        <div className={`${FIXTURE_LIST_CLASS}__matches-container`}>
          {sortedDateEntries.map(([date, fixturesForDate]) => {
            const formattedDate = format(parseISO(date), FIXTURE_MATCH_DATE);
            if (groupByCompetition) {
              return (
                <DateGroupByCompetition
                  key={date}
                  date={date}
                  formattedDate={formattedDate}
                  fixtures={fixturesForDate}
                  competitionsSort={competitionsSort}
                  style={style}
                  className={className}
                  {...sharedItemProps}
                />
              );
            }
            return (
              <DateGroupByDate
                key={date}
                date={date}
                formattedDate={formattedDate}
                fixtures={fixturesForDate}
                style={style}
                className={className}
                {...sharedItemProps}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

function FixtureListUI({
  fixtures,
  nameType,
  columns,
  groupByDate,
  showAttendance = false,
  attendancePlacement,
  groupByCompetition = false,
  competitionsSort,
  className,
  style: externalStyle,
  linkLabel,
  linkUrl,
  linkTargetBlank,
  linkVariant = LinkVariant.Bare,
  linkSize = LinkSize.m,
  isFixtureExpandable,
  title,
  sortByDate,
  allFixturesExpanded,
}: FixtureListUIProps) {
  const style: React.CSSProperties = { gridTemplateColumns: `repeat(${columns}, 1fr)`, ...(externalStyle ?? {}) };
  const getTeamName = (team: Match['homeTeam'] | Match['awayTeam']) => getTeamNameForType(team, nameType);
  const sortedFixtures = useMemo(() => sortFixtures(fixtures, getTeamName), [fixtures, nameType]);

  if (fixtures.length === 0) {
    return <FixtureListEmptyState className={className} />;
  }

  const header = (
    <SectionHeader
      title={title}
      action={
        linkLabel &&
        linkUrl && (
          <Link
            className={`${FIXTURE_LIST_CLASS}__link`}
            url={linkUrl}
            openInNewTab={!!linkTargetBlank}
            label={linkLabel}
            variant={linkVariant}
            size={linkSize}
          />
        )
      }
    />
  );

  const sharedItemProps = { showAttendance, attendancePlacement, nameType, isFixtureExpandable, allFixturesExpanded };

  if (!groupByDate) {
    return (
      <div className={FIXTURE_LIST_CLASS}>
        {header}
        <div className={`${FIXTURE_LIST_CLASS}__wrapper`}>
          <div className={`${FIXTURE_LIST_CLASS}__matches-container`}>
            <ul
              style={{ ...style, gridTemplateColumns: `repeat(${columns}, 1fr)` }}
              className={cs(FIXTURE_LIST_CLASS, className)}
            >
              {sortedFixtures.map((fixture) => (
                <FixtureItem key={fixture.id} fixture={fixture} {...sharedItemProps} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FixtureListGrouped
      sortedFixtures={sortedFixtures}
      sortByDate={sortByDate}
      groupByCompetition={groupByCompetition}
      competitionsSort={competitionsSort}
      style={style}
      className={className}
      header={header}
      {...sharedItemProps}
    />
  );
}

export default FixtureListUI;
